import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=logged out, obj=logged in
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      console.debug("auth/me not authenticated", err?.response?.status);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("token", data.token);
    // Fetch complete user profile (login response only returns minimal fields)
    try {
      const { data: full } = await api.get("/auth/me");
      setUser(full);
    } catch { setUser(data); }
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("token", data.token);
    // Fetch complete user profile (register response only returns minimal fields)
    try {
      const { data: full } = await api.get("/auth/me");
      setUser(full);
    } catch { setUser(data); }
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.error("logout failed", err); }
    localStorage.removeItem("token");
    setUser(false);
  };

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh: load, formatApiError }),
    [user, loading, load]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
